// THE MAGIC ROOM'S SHELF — WHERE EACH BOOK SITS IN THE SCENE, AND HOW BIG IT IS.
//
// Moved out of App.tsx by P2-7 in R37's currency (MOVE THE DATA, not the component): the round needed
// lines to pay for the pull-to-refresh mount, and this is 26 lines of pure data with no JSX in it.
// **MEASURED BEFORE THE MOVE (R9's lesson): nothing in `scripts/` read `MR_BOOKS` as source text**,
// so no gate went red or, worse, vacuous.
//
// The move is worth more than the lines, because this data carries a DOCUMENTED TRAP that no
// instrument could reach while it lived inside a 13,000-line TSX file. `MR_BOOKS_PORTRAIT` is read as
// `p.fx` with NO GUARD, so a `MagicView` present on the shelf and absent from the portrait map is a
// TypeError and a blank Magic Room on a phone. Out here `scripts/p27-pull-refresh.mjs` holds the
// biconditional: every book on the shelf has a portrait position, and every view in the type has an
// entry — which is R28/R29's fix shape (declare the other side, then assert the union).
//
// MR-L5: each book carries its SIZE (vh) and a z-depth. Sizes are big (Spell Book always largest);
// the upper row is brought DOWN (higher fy) and the render CLAMPS every book fully inside the safe
// area, so nothing crops at any width (owner's hard rule) — depth overlap is allowed, cropping is not.
import magicTilePetitionUrl from "../assets/magic-tile-petition.webp";
import magicTileKeysUrl from "../assets/magic-tile-keys.webp";
import magicTileSpellbookUrl from "../assets/magic-tile-spellbook.webp";
import magicTileOilsUrl from "../assets/magic-tile-oils.webp";
import magicTileMosesUrl from "../assets/magic-tile-moses.webp";
import magicTileRootbookUrl from "../assets/magic-tile-rootbook.webp";

// ITEM 12 — `cabinet` is the file cabinet. Like `notepaper` it is furniture at the room's EDGE rather
// than a book on the shelf, so it is a view with no `MR_BOOKS` entry; the portrait map below still
// owes it a position, because that map is read as `p.fx` with no guard.
//
// **THE COMMENT IS ABOVE THE UNION AND NOT INSIDE IT, AND THAT IS NOT A STYLE PREFERENCE.**
// `w12-magic-language.mjs` reads this roster by matching to the `;` and splitting on `|`, so a
// comment written between two arms becomes a "view" — and W12-2's biconditional then reports a
// perfectly-labelled room as carrying an unlabelled drawer whose name is a paragraph of prose. Caught
// in the round that did it (R9/R104's shape, on a parse rather than an anchor).
export type MagicView =
  | "tiles" | "petition" | "keys" | "spellbook" | "oils" | "moses" | "herbs" | "cauldron" | "notepaper"
  | "cabinet";

export type MagicBook = {
  key: MagicView; label: string; img: string; cls: string;
  fx: number; fy: number; vh: number; z: number;
};

export const MR_BOOKS: MagicBook[] = [
  // MR-L6: the Spell Book is brought DOWN toward the floor (grounded near the tray) and grown again.
  { key: "spellbook", label: "The Spellbook", img: magicTileSpellbookUrl, cls: "mr-book--spellbook", fx: 0.475, fy: 0.64, vh: 50, z: 6 },
  { key: "herbs", label: "Herbs & Tools", img: magicTileRootbookUrl, cls: "mr-book--herbs", fx: 0.235, fy: 0.44, vh: 33, z: 4 },
  { key: "keys", label: "Greater Keys of Solomon", img: magicTileKeysUrl, cls: "mr-book--keys", fx: 0.35, fy: 0.30, vh: 33, z: 3 },
  { key: "moses", label: "Books of Moses", img: magicTileMosesUrl, cls: "mr-book--moses", fx: 0.53, fy: 0.27, vh: 31, z: 2 },
  { key: "petition", label: "Petition", img: magicTilePetitionUrl, cls: "mr-book--petition", fx: 0.72, fy: 0.31, vh: 29, z: 3 },
  // MR-L6: Oils grown; its art alone does not name it, so it keeps an always-visible "Oils" label.
  { key: "oils", label: "Oils", img: magicTileOilsUrl, cls: "mr-book--oils", fx: 0.275, fy: 0.76, vh: 34, z: 5 },
];

// Portrait (phone) composition: centres as fractions of the ROOM box (not the art), a vertical drift.
// `Record<MagicView, …>` is TOTAL on purpose (R42's trick): a view added to the type cannot compile
// without a position here, which is the trap below turned into a compiler error.
export const MR_BOOKS_PORTRAIT: Record<MagicView, { fx: number; fy: number }> = {
  keys: { fx: 0.30, fy: 0.16 }, moses: { fx: 0.72, fy: 0.15 },
  spellbook: { fx: 0.5, fy: 0.39 }, petition: { fx: 0.28, fy: 0.60 },
  herbs: { fx: 0.72, fy: 0.61 }, oils: { fx: 0.42, fy: 0.83 },
  // R2: `notepaper` was added to MagicView and this map was not updated (TS2741, carried in the
  // typecheck baseline). It is not in MR_BOOKS today, so nothing reads it and nothing breaks — but
  // the read is `p.fx` with no guard, so the day notepaper becomes a book on the shelf it is a
  // TypeError and a blank Magic Room on a phone. Same {0,0} the other non-book views carry.
  tiles: { fx: 0, fy: 0 }, cauldron: { fx: 0, fy: 0 }, notepaper: { fx: 0, fy: 0 },
  // ITEM 12: the same {0,0} the other non-book views carry, for the same reason.
  cabinet: { fx: 0, fy: 0 },
};
